import React, { useState } from 'react';
import SimpleDialog, { SimpleDialogProps } from '../SimpleDialog/SimpleDialog';
import { ReviewerConflictType, Role, RoleDTO } from '../../../store/conference/types';
import { Checkbox, InputText, MultiSelectCheckbox } from '../../ui';
import { useTranslation } from 'react-i18next';
import ReviewerTypePicker from '../../ReviewerTypePicker';
import { doesHandleReviewers } from '../../../helpers/role';
import { connect } from 'react-redux';
import { AppState } from '../../../store/state';
import { selectTrackUsingKeywords } from '../../../store/selectors';
import { AppDispatch } from '../../../store/store';
import { getConflictTypeName } from '../../../helpers/translations';

interface Props extends SimpleDialogProps {
  initRole: RoleDTO;
  handleRoleOk: (role: RoleDTO) => void;
  roleById: { [key: string]: Role };
  trackUsingKeywords: boolean | null;
}

const RoleDialog: React.FC<Props> = ({ initRole, handleRoleOk, roleById, trackUsingKeywords, ...rest }) => {
  const { t, i18n } = useTranslation();
  const [role, setRole] = useState<RoleDTO>(initRole);

  const isEdition = role.id > 0;
  const isValid = !!role.description && (role.role_id == null || role.role_id > 0);
  const reviewerRoles = Object.values(roleById).filter((r) => r.type == 'reviewer');
  const useKeywordsAndDefined = !!trackUsingKeywords;

  const descriptionInputText = (
    <InputText
      label={t('Description')}
      placeholder="e.g., PC, SPC..."
      required
      defaultValue={role?.description}
      onChange={(e) => setRole({ ...role, description: e.target.value })}
      className="mb-5"
      fullWidth={true}
    />
  );

  const common = (
    <div>
      <div>
        <p className="mb-3">{t('Researcher details required')}</p>
        <div className="flex-row space-y-1">
          <div>
            <Checkbox
              label="DBLP URL"
              checked={role.dblp_required}
              onChange={(value) => setRole({ ...role, dblp_required: value.target.checked })}
            />
          </div>
          <div>
            <Checkbox
              label="Google Scholar URL"
              checked={role.gscholar_required}
              onChange={(value) => setRole({ ...role, gscholar_required: value.target.checked })}
            />
          </div>
          <div>
            <Checkbox
              label="ORCID URL"
              checked={role.orcid_required}
              onChange={(value) => setRole({ ...role, orcid_required: value.target.checked })}
            />
          </div>
        </div>
      </div>
      {useKeywordsAndDefined && (
        <>
          <hr className="mt-4 mb-4 opacity-20" style={{ borderTop: 'dotted 2px' }} />
          <div>
            <p className="mb-3">{t('Keywords relation')}</p>
            <Checkbox
              label={t('Requires choosing keywords')}
              checked={role.keywords_required}
              onChange={(value) => setRole({ ...role, keywords_required: value.target.checked })}
            />
          </div>
        </>
      )}
    </div>
  );

  const options: {
    id: ReviewerConflictType;
    label: string;
  }[] = [
    { id: 'bid', label: getConflictTypeName('bid', t) },
    { id: 'email-domain', label: getConflictTypeName('email-domain', t) },
    { id: 'affiliation', label: getConflictTypeName('affiliation', t) },
    { id: 'co-author', label: getConflictTypeName('co-author', t) },
  ];

  const getFormContent = (): JSX.Element | undefined => {
    if (isEdition) {
      switch (role.type) {
        case 'author':
          return common;
        case 'reviewer':
          return (
            <div>
              {descriptionInputText}
              <div className="mt-5">
                {reviewerRoles.length > 1 ? (
                  <>
                    <ReviewerTypePicker
                      fromRoleId={role.id}
                      toRoleId={role.role_id}
                      roleById={roleById}
                      onChange={(value) => {
                        const newRole: RoleDTO = { ...role, role_id: value };
                        if (!doesHandleReviewers(newRole)) {
                          newRole.summarized_views = false;
                        }
                        setRole(newRole);
                      }}
                    />
                    <Checkbox
                      className="mt-5"
                      label={t('Can access to summarized views')}
                      checked={role.summarized_views}
                      onChange={(value) => setRole({ ...role, summarized_views: value.target.checked })}
                    />
                  </>
                ) : (
                  <div>
                    <p className="mb-1.5">{t('Reviewer type') + ' *'}</p>
                    <p className="text-gray-600">{t('Handles submissions')}</p>
                  </div>
                )}
              </div>
              <hr className="mt-4 mb-4 opacity-20" style={{ borderTop: 'dotted 2px' }} />
              {common}
              <hr className="mt-4 mb-4 opacity-20" style={{ borderTop: 'dotted 2px' }} />
              <div>
                <p className="mb-3">{t('Paper status relation')}</p>
                <Checkbox
                  label={t('Can change paper status')}
                  checked={role.can_edit_paper_status}
                  onChange={(value) => setRole({ ...role, can_edit_paper_status: value.target.checked })}
                />
              </div>
              {!doesHandleReviewers(role) && (
                <>
                  <hr className="mt-4 mb-4 opacity-20" style={{ borderTop: 'dotted 2px' }} />
                  <div>
                    <p className="mb-3">{t('Activated conflicts')}</p>
                    <MultiSelectCheckbox
                      className="flex flex-col space-y-2"
                      options={options}
                      value={role.enabled_conflicts}
                      onChange={(value) => setRole({ ...role, enabled_conflicts: value as ReviewerConflictType[] })}
                    />
                  </div>
                </>
              )}
            </div>
          );
      }
    } else {
      return descriptionInputText;
    }
  };

  return (
    <SimpleDialog
      {...rest}
      title={isEdition ? t('Edit role') : t('New reviewer role')}
      handleOk={() => {
        // We call the external callback function depending on whether it's for creation or editing
        handleRoleOk(role);
      }}
      disableOkButton={!isValid}
    >
      {getFormContent()}
    </SimpleDialog>
  );
};

const mapStateToProps = (state: AppState) => ({
  trackUsingKeywords: selectTrackUsingKeywords(state),
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({});

export default connect(mapStateToProps, mapDispatchToProps)(RoleDialog);
